import { Component, HostListener, inject, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { Subject, Subscription, of } from 'rxjs';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { InsightsJobsService } from '../../../insights-jobs/insights-jobs.service';
import { DashboardDetailsDto, DashboardService, FilterDto, JobStatus, PdfPaperSize, ContentExportFormat, UserAppFeaturesDto } from "../../../services/api.services";
import { SnackService } from '../../../services/snack.service';
import { catchAppError, downloadFile } from '../../../shared/utils';
import { AppActions } from '../../../state/app';
import { AppContext } from 'src/app/app/context';
import { getUserState } from 'src/app/state/app/app.selectors';
import { toSignal } from '@angular/core/rxjs-interop';
import { logger } from '../../../pbir/utils/logger';

const LOGGER = logger('DashboardPdfExportDialog');

export interface DashboardPdfExportData {
  dashboard: DashboardDetailsDto
  filters?: FilterDto[];
  includeHeader?: boolean;
  includeFooter?: boolean;
  renderUnshared?: boolean;
};

@Component({
  templateUrl: 'pdf-export.dialog.html',
  styleUrls: ['pdf-export.dialog.scss'],
  standalone: false
})
export class DashboardPdfExportDialog {
  readonly appContext = inject(AppContext);
  readonly store = inject(Store);

  readonly userState = toSignal(this.store.pipe(select(getUserState)));

  static readonly defaultConfig = {
    width: '500px',
    minHeight: '440px',
    disableClose: true,
  };

  valid = true;
  exporting = false;
  paperSizes = pdfPaperSizes;

  constructor(
    private snackService: SnackService,
    private dashboardService: DashboardService,
    private insightsTasksService: InsightsJobsService,
    public dialogRef: MatDialogRef<DashboardPdfExportDialog>,
    @Inject(MAT_DIALOG_DATA) public data: DashboardPdfExportData) {
    this.dialogRef.addPanelClass('settings-modal-dialog-panel');
    this.dialogRef.afterClosed().subscribe(() => {
      this.abortJob();
    });
  }

  ngOnInit() {
    this.paperSize = this.userState()?.defaultPdfPaperSize ?? PdfPaperSize.A4;
  }

  dashboard = this.data.dashboard;

  paperSize = PdfPaperSize.A4;
  layoutOptions = true;
  includeHeader = this.data.includeHeader ?? true;
  includeFooter = this.data.includeFooter ?? true;

  failed = false;
  success = false;
  downloadUrl: string;
  fileName: string;
  currentJobId: number;

  abort$: Subject<void>;
  aborted = false;
  exportSub: Subscription;

  private abortJob() {
    this.aborted = true;
    this.abort$?.next();

    this.exportSub?.unsubscribe();
    this.exportSub = null;

    if (this.currentJobId) {
      this.dashboardService.cancelFileExport(this.currentJobId).subscribe();
      this.currentJobId = 0;
    }
  }

  cancel() {
    this.abortJob();

    this.dialogRef.close();
  }

  next() {
    if (this.exporting)
      return false;

    if (this.success) {
      this.dialogRef.close();
    }
    else {
      this.export();
    }
  }

  private export() {
    this.abortJob();

    this.aborted = false;
    this.abort$ = new Subject<void>();

    this.exporting = true;
    this.failed = false;
    this.success = false;

    this.store.dispatch(AppActions.updateUserState({
      userState: {
        defaultPdfPaperSize: this.paperSize
      }
    }));


    this.exportSub = this.dashboardService.exportDashboardToPdf(this.dashboard.id, {
      paperSize: this.paperSize,
      includeHeader: this.includeHeader,
      includeFooter: this.includeFooter,
      filters: this.data.filters,
      renderUnshared: this.data.renderUnshared,
      format: ContentExportFormat.Pdf    
    }).pipe(
      takeUntil(this.abort$),
      tap(({ id }) => this.currentJobId = id),
      switchMap(job => {
        return this.insightsTasksService.jobStarted(job.id).pipe(
          takeUntil(this.abort$),
          filter(s => s.completed),
          switchMap(s => {
            if (s.job.status === JobStatus.Failed || s.state.getStateFailed) {
              this.failed = true;
              this.success = false;
              this.exporting = false;

              return of(null);
            }
            else {
              return this.dashboardService.getExportDashboardToPdfResult(job.id).pipe(
                takeUntil(this.abort$),
                tap(result => {
                  if (this.aborted)
                    return;

                  this.abortJob();

                  this.downloadUrl = result.fileUrl;
                  this.fileName = result.fileName;

                  this.failed = false;
                  this.success = true;
                  this.exporting = false;
                  this.currentJobId = null;

                  try {
                    downloadFile(this.downloadUrl, this.fileName);
                  }
                  catch (err) {
                    LOGGER.error('downloadFile failed', err);
                  }
                })
              );
            }
          })
        )
      }),
      catchAppError(error => {
        this.abortJob();

        this.failed = true;
        this.success = false;
        this.exporting = false;

        LOGGER.error(error);

        return of(null);
      }, { showErrorSnack: false })
    ).subscribe();
  }

  @HostListener('window:beforeunload', ['$event'])
  onWindowBeforeUnload($event: Event) {
    if (this.exporting) {
      this.snackService.open('Leaving this page will cancel the export.', {
        panelClass: 'trg-snack-warning',
        verticalPosition: 'bottom',
      });

      $event.returnValue = false;
    }
  }
}

export const pdfPaperSizes = [
  {
    id: PdfPaperSize.A4,
    title: 'A4 (8.3" x 11.7")',
    titleForImage: 'A4',
  },
  {
    id: PdfPaperSize.Letter,
    title: 'Letter (8.5" x 11")',
    titleForImage: 'Letter',
  }
];
